import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import { useLanguage } from '../contexts/LanguageContext';
import { Dialog, DialogContent, DialogTitle, DialogDescription } from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Avatar, AvatarImage, AvatarFallback } from './ui/avatar';
import { Heart, MessageCircle, UserPlus, Sparkles, Loader2, Grid3x3, ScrollText, BookOpen, Search, X, Share2 } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import { mediaUrl } from '../services/postsApi';

const API = process.env.REACT_APP_BACKEND_URL;

// Public, read-only Instagram-style Community explore for logged-out visitors.
const PublicExplorePage = () => {
  const navigate = useNavigate();
  const { language } = useLanguage();
  const es = language === 'es';

  const [tab, setTab] = useState('posts');
  const [posts, setPosts] = useState([]);
  const [testimonies, setTestimonies] = useState([]);
  const [tray, setTray] = useState([]);
  const [loading, setLoading] = useState(true);
  const [active, setActive] = useState(null); // open post in lightbox
  const [query, setQuery] = useState('');

  useEffect(() => {
    let cancelled = false;
    // Load posts first and stop the spinner as soon as they arrive; tray &
    // testimonies fill in independently so the grid never blocks on them.
    axios.get(`${API}/api/community/posts/explore`).then((r) => r.data.posts).catch(() => [])
      .then((p) => { if (!cancelled) { setPosts(p || []); setLoading(false); } });
    axios.get(`${API}/api/community/posts/testimonies`).then((r) => r.data.posts).catch(() => [])
      .then((t) => { if (!cancelled) setTestimonies(t || []); });
    axios.get(`${API}/api/community/stories/tray`).then((r) => r.data.tray).catch(() => [])
      .then((tr) => { if (!cancelled) setTray(tr || []); });
    return () => { cancelled = true; };
  }, []);

  const promptSignup = (e) => {
    e?.stopPropagation?.();
    toast(es ? 'Únete para participar en la conversación' : 'Sign up to join the conversation', {
      description: es ? 'Crea tu cuenta gratis de Lumera.' : 'Create your free Lumera account.',
      action: { label: es ? 'Registrarse' : 'Sign up', onClick: () => navigate('/login') },
    });
  };

  // Invite link points at the server-rendered share page so previews unfurl
  // correctly for friends on any app (even non-JS crawlers).
  const shareCommunity = async () => {
    const url = `${window.location.origin}/api/community/share/explore${es ? '?lang=es' : ''}`;
    const shareData = {
      title: es ? 'Comunidad de Lumera' : 'Lumera Community',
      text: es ? 'Mira la comunidad de Lumera 🙌' : 'Check out the Lumera community 🙌',
      url,
    };
    try {
      if (navigator.share) {
        await navigator.share(shareData);
        return;
      }
    } catch (err) {
      if (err?.name === 'AbortError') return;
    }
    try {
      await navigator.clipboard.writeText(url);
      toast.success(es ? 'Enlace copiado — ¡compártelo con tus amigos!' : 'Link copied — share it with friends!');
    } catch {
      toast.info(url);
    }
  };

  const TOPICS = [
    { en: 'Peace', es: 'Paz' },
    { en: 'Healing', es: 'Sanidad' },
    { en: 'Gratitude', es: 'Gratitud' },
    { en: 'Hope', es: 'Esperanza' },
    { en: 'Faith', es: 'Fe' },
    { en: 'Love', es: 'Amor' },
    { en: 'Strength', es: 'Fortaleza' },
  ];

  const likeCount = (p) => (p.likes_count ?? (p.likes?.length || 0));
  const withImage = (list) => list.filter((p) => p.image_url);
  const matchesQuery = (p) => {
    const q = query.trim().toLowerCase();
    if (!q) return true;
    return (
      (p.caption || '').toLowerCase().includes(q) ||
      (p.verse_reference || '').toLowerCase().includes(q) ||
      (p.author_name || '').toLowerCase().includes(q)
    );
  };
  const grid = React.useMemo(() => {
    let base;
    if (tab === 'testimonies') base = withImage(testimonies);
    else if (tab === 'verses') base = withImage(posts).filter((p) => p.verse_reference);
    else if (tab === 'loved') base = [...withImage(posts)].sort((a, b) => likeCount(b) - likeCount(a));
    else base = withImage(posts);
    return base.filter(matchesQuery);
  }, [tab, posts, testimonies, query]);

  const TABS = [
    { id: 'posts', icon: Grid3x3, en: 'All', es: 'Todo' },
    { id: 'verses', icon: BookOpen, en: 'Verses', es: 'Versículos' },
    { id: 'testimonies', icon: ScrollText, en: 'Testimonies', es: 'Testimonios' },
    { id: 'loved', icon: Heart, en: 'Most loved', es: 'Más queridos' },
  ];

  return (
    <div className="min-h-screen bg-background" data-testid="public-explore-page">
      <Helmet>
        <title>{es ? 'Explora la comunidad de Lumera' : 'Explore the Lumera Community'}</title>
        <meta name="description" content={es
          ? 'Descubre versículos, testimonios y oraciones respondidas compartidas por creyentes en Lumera. Únete gratis para publicar, dar me gusta y comentar.'
          : 'Discover verses, testimonies and answered prayers shared by believers on Lumera. Join free to post, like and comment.'} />
        <meta property="og:type" content="website" />
        <meta property="og:site_name" content="Lumera" />
        <meta property="og:title" content={es ? 'Explora la comunidad de Lumera' : 'Explore the Lumera Community'} />
        <meta property="og:description" content={es
          ? 'Versículos, testimonios y oraciones respondidas de una comunidad global de fe.'
          : 'Verses, testimonies and answered prayers from a global community of faith.'} />
        <meta property="og:image" content={`${window.location.origin}/og-explore.png`} />
        <meta property="og:image:width" content="1264" />
        <meta property="og:image:height" content="848" />
        <meta property="og:url" content={`${window.location.origin}/explore`} />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={es ? 'Explora la comunidad de Lumera' : 'Explore the Lumera Community'} />
        <meta name="twitter:description" content={es
          ? 'Versículos, testimonios y oraciones respondidas de una comunidad global de fe.'
          : 'Verses, testimonies and answered prayers from a global community of faith.'} />
        <meta name="twitter:image" content={`${window.location.origin}/og-explore.png`} />
        <link rel="canonical" href={`${window.location.origin}/explore`} />
      </Helmet>
      {/* Top bar */}
      <header className="sticky top-0 z-30 border-b border-border bg-background/85 backdrop-blur">
        <div className="mx-auto flex max-w-5xl items-center justify-between px-4 py-3">
          <button onClick={() => navigate('/')} className="flex items-baseline gap-2" data-testid="explore-logo">
            <span className="font-display text-xl font-semibold tracking-tight">Lumera</span>
            <span className="text-xs text-muted-foreground">{es ? 'Comunidad' : 'Community'}</span>
          </button>
          <div className="flex items-center gap-2">
            <Button variant="ghost" size="sm" onClick={shareCommunity} data-testid="explore-share-community" className="hidden sm:inline-flex">
              <Share2 className="mr-1.5 h-4 w-4" /> {es ? 'Compartir' : 'Share'}
            </Button>
            <Button variant="ghost" size="sm" onClick={() => navigate('/login')} data-testid="explore-signin">
              {es ? 'Iniciar sesión' : 'Sign in'}
            </Button>
            <Button size="sm" onClick={() => navigate('/login')} data-testid="explore-join" className="bg-[#B0871F] text-[#0F1A2E] hover:bg-[#9A731A]">
              {es ? 'Únete gratis' : 'Join free'}
            </Button>
          </div>
        </div>
      </header>

      <main className="mx-auto max-w-5xl px-4 pb-24 pt-6">
        <div className="mb-6">
          <h1 className="font-display text-3xl font-medium tracking-tight sm:text-4xl">
            {es ? 'Explora la comunidad' : 'Explore the Community'}
          </h1>
          <p className="mt-2 max-w-xl text-sm text-muted-foreground">
            {es
              ? 'Mira cómo creyentes de todo el mundo comparten versículos, testimonios y oraciones respondidas. Únete gratis para publicar, dar me gusta y comentar.'
              : 'See how believers around the world share verses, testimonies and answered prayers. Join free to post, like and comment.'}
          </p>

          {/* Search */}
          <div className="relative mt-4 max-w-md">
            <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
            <Input
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder={es ? 'Busca por versículo, tema o palabra…' : 'Search by verse, topic or keyword…'}
              className="pl-9 pr-9"
              data-testid="explore-search-input"
            />
            {query && (
              <button
                onClick={() => setQuery('')}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                data-testid="explore-search-clear"
                aria-label={es ? 'Borrar búsqueda' : 'Clear search'}
              >
                <X className="h-4 w-4" />
              </button>
            )}
          </div>
        </div>

        {/* Stories tray teaser */}
        {tray.length > 0 && (
          <div className="mb-6 flex gap-4 overflow-x-auto pb-1" data-testid="explore-stories-tray">
            {tray.slice(0, 12).map((s, i) => (
              <button key={s.user_id || i} onClick={promptSignup} className="flex shrink-0 flex-col items-center gap-1" data-testid="explore-story-avatar">
                <span className="block h-16 w-16 rounded-full bg-gradient-to-tr from-amber-400 to-fuchsia-500 p-[2.5px]">
                  <Avatar className="h-full w-full border-2 border-background">
                    {s.picture ? <AvatarImage src={s.picture} /> : null}
                    <AvatarFallback>{(s.name || '?').slice(0, 2).toUpperCase()}</AvatarFallback>
                  </Avatar>
                </span>
                <span className="max-w-[64px] truncate text-[11px] text-muted-foreground">{s.name || 'Lumera'}</span>
              </button>
            ))}
          </div>
        )}

        {/* Trending topics */}
        <div className="mb-4 flex flex-wrap items-center gap-2" data-testid="explore-topics">
          <span className="text-xs font-semibold uppercase tracking-widest text-muted-foreground">
            {es ? 'Temas' : 'Topics'}
          </span>
          {TOPICS.map((tp) => {
            const label = es ? tp.es : tp.en;
            const activeTopic = query.trim().toLowerCase() === label.toLowerCase();
            return (
              <button
                key={tp.en}
                onClick={() => setQuery(activeTopic ? '' : label)}
                className={`rounded-full border px-3 py-1 text-xs font-medium transition-colors ${activeTopic ? 'border-[#B0871F] bg-[#B0871F] text-[#0F1A2E]' : 'border-border text-muted-foreground hover:border-[#B0871F] hover:text-foreground'}`}
                data-testid={`explore-topic-${tp.en.toLowerCase()}`}
              >
                {label}
              </button>
            );
          })}
        </div>

        {/* Filter tabs */}
        <div className="mb-4 flex items-center justify-center gap-4 overflow-x-auto border-b border-border sm:gap-6" data-testid="explore-tabs">
          {TABS.map((tb) => {
            const Icon = tb.icon;
            return (
              <button
                key={tb.id}
                onClick={() => setTab(tb.id)}
                className={`flex shrink-0 items-center gap-1.5 pb-2 text-xs uppercase tracking-widest ${tab === tb.id ? 'border-b-2 border-foreground font-semibold text-foreground' : 'text-muted-foreground'}`}
                data-testid={`explore-tab-${tb.id}`}
              >
                <Icon className="h-3.5 w-3.5" /> {es ? tb.es : tb.en}
              </button>
            );
          })}
        </div>

        {loading ? (
          <div className="flex justify-center py-20"><Loader2 className="h-6 w-6 animate-spin text-muted-foreground" /></div>
        ) : grid.length === 0 ? (
          <p className="py-20 text-center text-sm text-muted-foreground" data-testid="explore-empty">
            {query
              ? (es ? `Sin resultados para “${query}”. Prueba otra palabra o versículo.` : `No results for “${query}”. Try another word or verse.`)
              : (es ? 'Aún no hay nada aquí. ¡Sé el primero en compartir!' : 'Nothing here yet. Be the first to share!')}
          </p>
        ) : (
          <div className="grid grid-cols-3 gap-1 sm:gap-2" data-testid="explore-grid">
            {grid.map((p) => (
              <button key={p.id} onClick={() => setActive(p)} className="group relative aspect-square overflow-hidden bg-muted" data-testid={`explore-post-${p.id}`}>
                <img src={mediaUrl(p.image_url)} alt={p.caption || ''} className="h-full w-full object-cover transition-transform group-hover:scale-105" onError={(e) => { e.currentTarget.closest('button').style.display = 'none'; }} />
                <span className="absolute inset-0 hidden items-center justify-center gap-4 bg-black/40 text-white group-hover:flex">
                  <span className="flex items-center gap-1 text-sm font-semibold"><Heart className="h-4 w-4 fill-white" /> {(p.likes_count ?? (p.likes?.length || 0))}</span>
                  <span className="flex items-center gap-1 text-sm font-semibold"><MessageCircle className="h-4 w-4 fill-white" /> {p.comments_count || 0}</span>
                </span>
              </button>
            ))}
          </div>
        )}
      </main>

      {/* Sticky join CTA */}
      <div className="fixed inset-x-0 bottom-0 z-30 border-t border-border bg-background/95 px-4 py-3 backdrop-blur">
        <div className="mx-auto flex max-w-5xl items-center justify-between gap-3">
          <p className="text-sm text-muted-foreground"><Sparkles className="mr-1 inline h-4 w-4 text-[#B0871F]" />{es ? 'Únete a la comunidad de Lumera gratis' : 'Join the Lumera community for free'}</p>
          <Button onClick={() => navigate('/login')} className="bg-[#B0871F] text-[#0F1A2E] hover:bg-[#9A731A]" data-testid="explore-join-sticky">
            <UserPlus className="mr-2 h-4 w-4" /> {es ? 'Únete gratis' : 'Join free'}
          </Button>
        </div>
      </div>

      {/* Post lightbox */}
      <Dialog open={!!active} onOpenChange={(v) => { if (!v) setActive(null); }}>
        <DialogContent className="max-w-lg p-0 overflow-hidden" data-testid="explore-post-lightbox">
          <DialogTitle className="sr-only">{active?.caption || (es ? 'Publicación' : 'Post')}</DialogTitle>
          <DialogDescription className="sr-only">{es ? 'Vista de publicación pública' : 'Public post view'}</DialogDescription>
          {active && (
            <div>
              <div className="flex items-center gap-2 px-4 py-3">
                <Avatar className="h-8 w-8">
                  {active.author_picture ? <AvatarImage src={active.author_picture} /> : null}
                  <AvatarFallback>{(active.author_name || '?').slice(0, 2).toUpperCase()}</AvatarFallback>
                </Avatar>
                <span className="text-sm font-semibold">{active.author_name || 'Someone'}</span>
              </div>
              <img src={mediaUrl(active.image_url)} alt={active.caption || ''} className="w-full" />
              <div className="space-y-3 px-4 py-3">
                <div className="flex items-center gap-4 text-muted-foreground">
                  <button onClick={promptSignup} className="flex items-center gap-1 hover:text-rose-500" data-testid="explore-like-btn"><Heart className="h-5 w-5" /> {(active.likes_count ?? (active.likes?.length || 0))}</button>
                  <button onClick={promptSignup} className="flex items-center gap-1 hover:text-foreground" data-testid="explore-comment-btn"><MessageCircle className="h-5 w-5" /> {active.comments_count || 0}</button>
                </div>
                {active.caption && (
                  <p className="text-sm"><span className="font-semibold">{active.author_name || 'Someone'}</span> {active.caption}</p>
                )}
                {active.verse_reference && <p className="text-xs font-semibold text-[#B0871F]">{active.verse_reference}</p>}
                <Button onClick={promptSignup} variant="outline" className="w-full" data-testid="explore-signup-prompt">
                  <UserPlus className="mr-2 h-4 w-4" /> {es ? 'Regístrate para unirte a la conversación' : 'Sign up to join the conversation'}
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default PublicExplorePage;
